import React, { useEffect, useState } from 'react';
import { Box, Button, Tab, Tabs, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ClientWorkspaceOverview from './ClientWorkspaceOverview';
import ClientWorkspaceAI from './ClientWorkspaceAI';
import { requestUnderstandLeave } from '../../utils/understandLeave';

const TABS = {
  OVERVIEW: 'overview',
  AI: 'ai',
};

/**
 * Layout-agnostic Client Workspace content.
 * Reusable inside a desktop drawer now, or a full-screen mobile page later.
 */
const ClientWorkspace = ({ product, company, initialTab = TABS.OVERVIEW }) => {
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [product?._id, initialTab]);

  if (!product) return null;

  const companyName = company?.companyName || product.company?.companyName;

  return (
    <Box display='flex' flexDirection='column' height='100%' minHeight={0}>
      <Box px={3} pt={2} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => {
            if (value === activeTab) return;
            if (activeTab === TABS.AI && value !== TABS.AI) {
              requestUnderstandLeave(() => setActiveTab(value));
              return;
            }
            setActiveTab(value);
          }}
          sx={{
            minHeight: 40,
            '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 },
          }}
        >
          <Tab label='Overview' value={TABS.OVERVIEW} />
          <Tab label='AI Assistant' value={TABS.AI} />
        </Tabs>
      </Box>

      <Box
        flex={1}
        minHeight={0}
        display='flex'
        flexDirection='column'
        overflow={activeTab === TABS.OVERVIEW ? 'auto' : 'hidden'}
        px={activeTab === TABS.OVERVIEW ? 3 : 0}
        py={activeTab === TABS.OVERVIEW ? 2 : 0}
      >
        {activeTab === TABS.OVERVIEW ? (
          <ClientWorkspaceOverview product={product} company={company} />
        ) : (
          <ClientWorkspaceAI companyName={companyName || 'this company'} product={product} company={company} />
        )}
      </Box>

      {activeTab === TABS.OVERVIEW && (
        <Box
          px={3}
          py={2}
          borderTop={1}
          borderColor='divider'
          bgcolor='#eef5ff'
          display='flex'
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent='space-between'
          gap={2}
          flexDirection={{ xs: 'column', sm: 'row' }}
        >
          <Typography variant='body2' color='text.secondary'>
            <b>Ready to get started?</b>
            <br />
            Use AI Assistant to generate intros, replies, and more.
          </Typography>
          <Button
            variant='contained'
            startIcon={<AutoAwesomeIcon />}
            onClick={() => setActiveTab(TABS.AI)}
            sx={{ flexShrink: 0, textTransform: 'none', fontWeight: 600 }}
          >
            Go to AI Assistant
          </Button>
        </Box>
      )}
    </Box>
  );
};

export { TABS };
export default ClientWorkspace;
